import React from 'react';
import type { GeneratedBarcode } from '@/api/models';
import { getUser } from '@/utils/authSession';
import { downloadBlob, xlsxBlobOf } from '@/utils/fileDownload';
import {
    buildLabelListWorkbook,
    type LabelListLabels,
} from '@/utils/labelList';
import { labelListDateOf } from './labelsCardMeta';

/** The label list's words, which the View translates (rulebook §7). */
export type LabelListCopy = {
    labels: LabelListLabels;
    /** The saved file's name, for the date in `fileDate`. */
    fileName: string;
};

export type LabelListDownload = {
    /**
     * Builds the `.xlsx` from the rows in `copy`'s words and downloads it;
     * ignored while one runs.
     */
    onDownload: (copy: LabelListCopy) => void;
    /** The date the file is named by (`YYYY-MM-DD`). */
    fileDate: string;
    isDownloading: boolean;
    /** The last download could not be made. */
    downloadFailed: boolean;
};

/**
 * The label list's **Download label list (.xlsx)**, for the catalog import's
 * Result and the Products page's Barcode issues: the workbook built in the
 * browser from `generatedBarcodes` (`buildLabelListWorkbook`, exceljs loaded
 * on demand), in the words the View passes, and saved under the name it
 * passes (`label-list-<fileDate>.xlsx`).
 *
 * One run at a time: a second press while one is under way is ignored, even
 * before the pressed state renders. A failure is kept until the next press.
 * The run belongs to whoever pressed (review of #241, finding 14): when the
 * owner unmounted (the operator left the step or the tab) or another user
 * signed in while exceljs loaded, the file is not saved and no state is set.
 */
export const useLabelListDownload = (
    rows: readonly GeneratedBarcode[],
    createdAt: string,
): LabelListDownload => {
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [downloadFailed, setDownloadFailed] = React.useState(false);
    const isRunning = React.useRef(false);
    const isMounted = React.useRef(true);

    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const onDownload = React.useCallback(
        ({ labels, fileName }: LabelListCopy) => {
            if (isRunning.current) {
                return;
            }
            isRunning.current = true;
            setIsDownloading(true);
            setDownloadFailed(false);
            const owner = getUser()?.id ?? null;
            const isStillOwner = () =>
                isMounted.current && (getUser()?.id ?? null) === owner;

            void (async () => {
                try {
                    const workbook = await buildLabelListWorkbook(rows, labels);
                    const blob = await xlsxBlobOf(workbook);
                    if (isStillOwner()) {
                        downloadBlob(blob, fileName);
                    }
                } catch (error) {
                    // The console line is for whoever is debugging; the
                    // notice is for whoever pressed the button.
                    console.error('[label list] download failed:', error);
                    if (isStillOwner()) {
                        setDownloadFailed(true);
                    }
                } finally {
                    isRunning.current = false;
                    if (isMounted.current) {
                        setIsDownloading(false);
                    }
                }
            })();
        },
        [rows],
    );

    return {
        onDownload,
        fileDate: createdAt === '' ? '' : labelListDateOf(createdAt),
        isDownloading,
        downloadFailed,
    };
};
